/**
 * Sparkline Component
 * SVG-based mini line chart for latency history visualization
 */

import { useMemo } from 'react';
import { LatencySample, LATENCY_THRESHOLDS, getLatencyStatus } from '../types/perf.types';

interface SparklineProps {
  samples: LatencySample[];
  width?: number;
  height?: number;
  showThresholds?: boolean;
  showDots?: boolean;
  className?: string;
}

export function Sparkline({
  samples,
  width = 120,
  height = 32,
  showThresholds = true,
  showDots = false,
  className = '',
}: SparklineProps) {
  const chartData = useMemo(() => {
    // Padding for the chart
    const paddingX = 4;
    const paddingY = 4;
    const chartWidth = width - paddingX * 2;
    const chartHeight = height - paddingY * 2;

    if (samples.length === 0) {
      return {
        path: '',
        dots: [] as { x: number; y: number; value: number; status: string }[],
        maxValue: 500,
        greenY: height / 2,
        yellowY: height / 3,
        paddingX,
        chartWidth,
      };
    }

    // Extract latency values
    const values = samples.map((s) => s.latencyMs);

    // Determine scale
    const minValue = 0;
    const maxValue = Math.max(...values, LATENCY_THRESHOLDS.YELLOW + 100);

    // Calculate points
    const points = values.map((value, index) => {
      const x = paddingX + (index / Math.max(values.length - 1, 1)) * chartWidth;
      const y = paddingY + chartHeight - ((value - minValue) / (maxValue - minValue)) * chartHeight;
      return { x, y, value };
    });

    // Create SVG path
    const path =
      points.length > 0
        ? points.reduce((acc, point, index) => {
            if (index === 0) return `M ${point.x} ${point.y}`;
            return `${acc} L ${point.x} ${point.y}`;
          }, '')
        : '';

    // Calculate threshold lines
    const greenY =
      paddingY +
      chartHeight -
      ((LATENCY_THRESHOLDS.GREEN - minValue) / (maxValue - minValue)) * chartHeight;
    const yellowY =
      paddingY +
      chartHeight -
      ((LATENCY_THRESHOLDS.YELLOW - minValue) / (maxValue - minValue)) * chartHeight;

    return {
      path,
      dots: points.map((p, i) => ({
        ...p,
        status: samples[i]?.status || 'unknown',
      })),
      maxValue,
      greenY: Math.max(paddingY, Math.min(greenY, height - paddingY)),
      yellowY: Math.max(paddingY, Math.min(yellowY, height - paddingY)),
      paddingX,
      chartWidth,
    };
  }, [samples, width, height]);

  // Get the current status for line color
  const currentStatus = samples.length > 0 ? samples[samples.length - 1].status : 'unknown';

  const lineColor = useMemo(() => {
    switch (currentStatus) {
      case 'green':
        return '#22c55e'; // green-500
      case 'yellow':
        return '#eab308'; // yellow-500
      case 'red':
        return '#ef4444'; // red-500
      default:
        return '#9ca3af'; // gray-400
    }
  }, [currentStatus]);

  if (samples.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-text-secondary text-xs ${className}`}
        style={{ width, height }}
      >
        No data
      </div>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      className={`overflow-visible ${className}`}
      viewBox={`0 0 ${width} ${height}`}
    >
      {/* Background */}
      <rect x={0} y={0} width={width} height={height} fill="transparent" rx={4} />

      {/* Threshold zones (subtle background) */}
      {showThresholds && (
        <>
          {/* Red zone (top) */}
          <rect
            x={chartData.paddingX}
            y={4}
            width={chartData.chartWidth}
            height={Math.max(0, chartData.yellowY - 4)}
            fill="rgba(239, 68, 68, 0.05)"
          />
          {/* Yellow zone */}
          <rect
            x={chartData.paddingX}
            y={chartData.yellowY}
            width={chartData.chartWidth}
            height={Math.max(0, chartData.greenY - chartData.yellowY)}
            fill="rgba(234, 179, 8, 0.05)"
          />
          {/* Green zone (bottom) */}
          <rect
            x={chartData.paddingX}
            y={chartData.greenY}
            width={chartData.chartWidth}
            height={Math.max(0, height - 4 - chartData.greenY)}
            fill="rgba(34, 197, 94, 0.05)"
          />

          {/* Threshold lines */}
          <line
            x1={chartData.paddingX}
            y1={chartData.greenY}
            x2={width - chartData.paddingX}
            y2={chartData.greenY}
            stroke="rgba(34, 197, 94, 0.3)"
            strokeWidth={1}
            strokeDasharray="2,2"
          />
          <line
            x1={chartData.paddingX}
            y1={chartData.yellowY}
            x2={width - chartData.paddingX}
            y2={chartData.yellowY}
            stroke="rgba(234, 179, 8, 0.3)"
            strokeWidth={1}
            strokeDasharray="2,2"
          />
        </>
      )}

      {/* Line chart */}
      <path
        d={chartData.path}
        fill="none"
        stroke={lineColor}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Dots at each data point */}
      {showDots &&
        chartData.dots.map((dot, index) => (
          <circle
            key={index}
            cx={dot.x}
            cy={dot.y}
            r={2}
            fill={
              dot.status === 'green'
                ? '#22c55e'
                : dot.status === 'yellow'
                  ? '#eab308'
                  : dot.status === 'red'
                    ? '#ef4444'
                    : '#9ca3af'
            }
          />
        ))}

      {/* Latest value dot (always shown) */}
      {chartData.dots.length > 0 && (
        <circle
          cx={chartData.dots[chartData.dots.length - 1].x}
          cy={chartData.dots[chartData.dots.length - 1].y}
          r={3}
          fill={lineColor}
          stroke="white"
          strokeWidth={1}
        />
      )}
    </svg>
  );
}

/**
 * Compact sparkline with inline value display
 */
interface SparklineWithValueProps extends SparklineProps {
  value: number | null;
  label?: string;
}

export function SparklineWithValue({ value, label, samples, ...props }: SparklineWithValueProps) {
  const status = getLatencyStatus(value);

  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-xs text-text-secondary w-16 truncate">{label}</span>}
      <Sparkline samples={samples} {...props} />
      <span
        className={`text-sm font-mono w-14 text-right ${
          status === 'green'
            ? 'text-green-600'
            : status === 'yellow'
              ? 'text-yellow-600'
              : status === 'red'
                ? 'text-red-600'
                : 'text-gray-400'
        }`}
      >
        {value !== null ? `${Math.round(value)}ms` : '-'}
      </span>
    </div>
  );
}

export default Sparkline;
